<template>
	<van-popup v-model="visible" position="bottom" round class="audit-search-picker">
		<!-- 头部 -->
		<div class="audit-search-picker__header">
			<span class="audit-search-picker__title">{{ title }}</span>
			<!-- <van-icon name="cross" class="audit-search-picker__close" @click="visible = false" /> -->
		</div>

		<!-- 搜索 -->
		<div class="audit-search-picker__search">
			<van-search
				v-model="keyword"
				:placeholder="placeholder"
				shape="round"
				background="transparent"
				@input="onSearch"
			/>
		</div>

		<!-- 列表 -->
		<div class="audit-search-picker__body">
			<div
				class="audit-search-picker__item"
				v-for="item in list"
				:key="item.id || item.name"
				:class="{ 'is-selected': isSelected(item) }"
				@click="select(item)"
			>
				<div class="audit-search-picker__icon">
					<van-icon :name="icon" />
				</div>
				<div class="audit-search-picker__name">{{ getLabel(item) }}</div>
				<van-icon name="success" v-if="isSelected(item)" class="audit-search-picker__check" />
			</div>
			<van-empty v-if="!list.length && searched && !loading" description="暂无数据" />
			<van-loading v-if="loading" class="audit-search-picker__loading" size="20px">搜索中...</van-loading>
		</div>

		<!-- 底部确认 -->
		<div class="audit-search-picker__footer">
			<div class="audit-search-picker__selected" :class="{ 'is-empty': !selectedItem }">
				<template v-if="selectedItem">已选：<span class="audit-search-picker__selected-name">{{ getLabel(selectedItem) }}</span></template>
				<template v-else>请搜索并选择{{ title.replace('选择', '') }}</template>
			</div>
			<van-button type="primary" block :disabled="!selectedItem" @click="handleConfirm">确定</van-button>
		</div>
	</van-popup>
</template>

<script>
// 远程搜索选择弹层（医院 / 银行复用）
// request: (keyword) => Promise<Array>，由父级调用对应接口并返回列表
export default {
	name: 'AuditSearchPicker',
	props: {
		value: {
			type: Boolean,
			default: false,
		},
		title: {
			type: String,
			default: '',
		},
		placeholder: {
			type: String,
			default: '请输入关键词搜索',
		},
		// 列表项显示字段（默认 name，可传 bank_name 等）
		labelKey: {
			type: String,
			default: 'name',
		},
		// 列表项图标
		icon: {
			type: String,
			default: 'shop-o',
		},
		request: {
			type: Function,
			default: () => Promise.resolve([]),
		},
	},
	data: () => ({
		keyword: '',
		searchTimer: null,
		list: [],
		loading: false,
		searched: false,
		selectedItem: null,
	}),
	computed: {
		visible: {
			get() {
				return this.value
			},
			set(val) {
				this.$emit('input', val)
			},
		},
	},
	watch: {
		visible(val) {
			if (val) {
				this.keyword = ''
				this.list = []
				this.searched = false
				this.selectedItem = null
			}
		},
	},
	methods: {
		onSearch() {
			if (this.searchTimer) clearTimeout(this.searchTimer)
			this.searchTimer = setTimeout(() => this.search(), 500)
		},
		search() {
			if (!this.keyword) {
				this.list = []
				this.searched = false
				return
			}
			this.loading = true
			this.request(this.keyword)
				.then((res) => {
					this.loading = false
					this.searched = true
					this.list = res || []
				})
				.catch(() => {
					this.loading = false
					this.searched = true
					this.list = []
				})
		},
		getLabel(item) {
			return item[this.labelKey] || item.name || ''
		},
		isSelected(item) {
			if (!this.selectedItem) return false
			return this.selectedItem === item || (item.id && this.selectedItem.id === item.id)
		},
		select(item) {
			this.selectedItem = item
		},
		handleConfirm() {
			if (!this.selectedItem) return
			this.visible = false
			this.$emit('confirm', this.selectedItem)
		},
	},
}
</script>

<style lang="less" scoped>
.audit-search-picker {
	display: flex;
	flex-direction: column;
	height: 70vh;

	&__header {
		position: relative;
		height: 48px;
		line-height: 48px;
		text-align: center;
		font-size: 16px;
		font-weight: 500;
		color: #1f2329;
		flex-shrink: 0;
	}

	&__close {
		position: absolute;
		top: 0;
		right: 16px;
		font-size: 18px;
		color: #8f959e;
	}

	&__search {
		flex-shrink: 0;

		::v-deep .van-search__content {
			background-color: #f5f6f7;
		}
	}

	&__body {
		flex: 1;
		overflow-y: auto;
		padding: 4px 16px;
	}

	&__item {
		display: flex;
		align-items: center;
		padding: 12px 4px;
		border-bottom: 1px solid #f5f6f7;

		&.is-selected {
			.audit-search-picker__name {
				color: var(--color-primary);
				font-weight: 500;
			}
		}
	}

	&__icon {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 34px;
		height: 34px;
		margin-right: 12px;
		border-radius: 8px;
		background-color: var(--color-primary-light);
		flex-shrink: 0;

		.van-icon {
			font-size: 18px;
			color: var(--color-primary);
		}
	}

	&__name {
		flex: 1;
		min-width: 0;
		font-size: 14px;
		color: #1f2329;
		word-break: break-all;
	}

	&__check {
		margin-left: 8px;
		font-size: 16px;
		color: var(--color-primary);
	}

	&__loading {
		padding-top: 40px;
	}

	// 底部确认
	&__footer {
		flex-shrink: 0;
		padding: 10px 16px calc(16px + constant(safe-area-inset-bottom));
		padding-bottom: calc(16px + env(safe-area-inset-bottom));
		border-top: 1px solid #f5f6f7;
		background-color: #fff;
	}

	&__selected {
		margin-bottom: 10px;
		font-size: 13px;
		text-align: center;
		color: #1f2329;

		&.is-empty {
			color: #8f959e;
		}
	}

	&__selected-name {
		color: var(--color-primary);
		font-weight: 500;
	}
}
</style>
